/**
 * ECSR-1059 — show only Loop's Checkout+ offer in the mini cart.
 *
 * The mini cart is the Amplify Power Slide Cart app's drawer. Inside its footer
 * (`.picky-cart__footer`) Amplify renders its own Return Coverage block
 * (`.amplify-rc-block`) plus its own checkout button (`.picky-cart__button`), and Loop's
 * app embed injects a `checkout-plus` widget alongside them. With both live the shopper is
 * offered shipping protection twice and given two ways to check out.
 *
 * This script flags a mini-cart footer that contains a Loop widget which has actually
 * rendered (non-zero box), by adding `loop-checkout-plus-only` to that footer, plus one
 * modifier per block that is safe to hide:
 *   - `loop-checkout-plus-hide-rc`     — the widget is NOT inside `.amplify-rc-block`
 *   - `loop-checkout-plus-hide-native` — the widget is NOT inside `.picky-cart__button`
 * `loop-checkout-plus-mini-cart.css` hangs the hiding rules off those classes and repeats the
 * guard in CSS with `:not(:has(checkout-plus))`.
 *
 * Where the widget actually lands (measured 2026-09-16, not assumed): on the ECSR-1059
 * preview theme (158463295653) with the mini cart open, the `checkout-plus` element in that
 * footer reports
 * `insideRcBlock: false`, `insidePickyButton: false`, parent `_buttonsWrapper_lx1p7_17`,
 * height 220; the footer's child order is `.amplify-rc-divider`, `.picky-totals`,
 * `.amplify-rc-block`, `._buttonsWrapper`. So today the widget is a SIBLING of
 * `.amplify-rc-block`, not a descendant of it. (The `checkout-selector` setting of
 * `.picky-cart__button` (ECSR-1060) belongs to Loop's separate on-store store-credit bar
 * embed — it tells that bar which button to trigger and does not position this widget.
 * Note the coupling that creates: the bar clicks the same `.picky-cart__button` this script
 * HIDES. Hiding is what keeps that click working; it must never become a removal. See
 * `docs/loop/checkout-plus-mini-cart.md`.)
 * The ancestry tests below are kept anyway: they are cheap, and they are what guarantees the
 * invariant that `.amplify-rc-block` and `.picky-cart__button` are never both hidden unless a
 * rendered `checkout-plus` is present in that footer — including if a future app update moves
 * the widget inside one of those blocks.
 *
 * Why a rendered-height test rather than a CSS `:has(checkout-plus)` rule: Amplify hides its
 * own checkout button whenever its Return Coverage block is active, so hiding that block
 * while Loop's widget merely exists in the DOM but has not painted would leave the mini cart
 * with no checkout button at all. A `checkout-plus` element that is present but empty or
 * suppressed (Loop's app embed disabled, script blocked, widget errored) has no box, so it
 * never trips the flag and Amplify's block stays exactly as it is today.
 *
 * Re-render safety: the drawer is re-rendered wholesale by Amplify on add / quantity change /
 * remove and on open/close, so the flag is recomputed from a MutationObserver and from a
 * ResizeObserver on each widget (which also catches the widget appearing, being hidden, or
 * collapsing to zero height after the DOM has settled). Classes are only ever set from the
 * animation-frame `sync()` pass, which measures the widget BEFORE hiding anything. That leaves
 * a sub-frame window after a re-render in which both protection offers can be visible; that
 * cosmetic flash is deliberately accepted (reviewer finding, ECSR-1059 review round 2). The
 * earlier synchronous pre-paint hide removed the flash but could hide Amplify's block and
 * button while Loop's widget had not yet painted, i.e. a mini cart with no checkout button at
 * all — a far worse failure than a duplicate offer.
 *
 * Cart state (reviewer finding, ECSR-1059 review round 1): hiding Amplify's block hides the
 * only "Continue without Protected Checkout" control, so it would be a billing problem if
 * coverage could already be applied to the cart at that point. It cannot on this store: the
 * only coverage products are Loop's own (`Package Protection + Return Shipping`, ACTIVE, and
 * `Free Return Shipping & Package Protection`, UNLISTED — both tagged `checkoutplus` /
 * `loop-returns`; the one re:do product is DRAFT). There is no Amplify-owned coverage product,
 * and the line is added at click time, not auto-applied. So any coverage already in the cart is
 * Loop's, and Loop's widget — the control that stays visible — is its remove control. This
 * script therefore does not mutate the cart. That assumption is recorded in
 * `docs/loop/checkout-plus-mini-cart.md` so it is re-checked before any Amplify app upgrade.
 *
 * Scoped to `.picky-cart__footer`, so the `checkout-plus` used on the /cart page is untouched.
 */
(function () {
  'use strict';

  var STATE_CLASS = 'loop-checkout-plus-only';
  var HIDE_RC_CLASS = 'loop-checkout-plus-hide-rc';
  var HIDE_NATIVE_CLASS = 'loop-checkout-plus-hide-native';
  var FOOTER_SELECTOR = '.picky-cart__footer';
  var WIDGET_SELECTOR = 'checkout-plus';
  var RELEVANT_SELECTOR = '.picky-cart__footer, checkout-plus';
  var RC_SELECTOR = '.amplify-rc-block';
  var NATIVE_SELECTOR = '.picky-cart__button';

  var resizeObserver =
    typeof ResizeObserver === 'function'
      ? new ResizeObserver(function () {
          schedule();
        })
      : null;
  var scheduled = false;
  // Widgets currently observed by `resizeObserver`, in `sync()` order. Re-observing every pass
  // would be self-sustaining: `observe()` delivers an initial notification for each new target,
  // which would schedule another `sync()`, which would re-observe, once per animation frame
  // forever (reviewer finding, ECSR-1059 review round 2). So only re-observe when the set
  // actually changes.
  var observedWidgets = [];

  function hasRendered(element) {
    // offsetHeight/offsetWidth are 0 for a widget that is absent from the layout, display:none,
    // or present but empty — all the states in which Amplify must keep its own offer.
    return !!element && element.offsetHeight > 0 && element.offsetWidth > 0;
  }

  // True when `element` is inside an ancestor matching `selector` that is still within `root`.
  function isInside(element, selector, root) {
    if (!element || !element.closest) return false;
    var match = element.closest(selector);
    return !!match && root.contains(match);
  }

  function clearFlags(footer) {
    footer.classList.remove(STATE_CLASS);
    footer.classList.remove(HIDE_RC_CLASS);
    footer.classList.remove(HIDE_NATIVE_CLASS);
  }

  // Adds the state classes for the blocks that do not contain the widget. Only ever called for a
  // widget that has already measured non-zero.
  function applyFlags(footer, widget) {
    // Widget nested inside Amplify's own checkout button: hide nothing at all. Amplify puts
    // `.amplify-rc-hide-native` on the footer whenever its Return Coverage feature is active, and
    // its own `!important` rule then hides `.picky-cart__button` — the very element the widget
    // would be living in. Hiding the RC block in that layout would leave the widget unpainted,
    // `hasRendered()` would read 0 on the next frame, the flags would clear and the widget would
    // repaint: a per-frame flicker with no usable checkout control. Today's measured layout (the
    // widget is a sibling of both blocks) never reaches this branch; it is a guard against a
    // future Amplify/Loop layout change.
    if (isInside(widget, NATIVE_SELECTOR, footer)) {
      clearFlags(footer);
      return;
    }

    var hideRc = !isInside(widget, RC_SELECTOR, footer);
    var hideNative = !isInside(widget, NATIVE_SELECTOR, footer);

    if (!hideRc && !hideNative) {
      clearFlags(footer);
      return;
    }

    var active = document.activeElement;
    var focusWasHidden =
      !!active &&
      footer.contains(active) &&
      !widget.contains(active) &&
      ((hideRc && isInside(active, RC_SELECTOR, footer)) ||
        (hideNative && isInside(active, NATIVE_SELECTOR, footer)));

    footer.classList.add(STATE_CLASS);
    footer.classList[hideRc ? 'add' : 'remove'](HIDE_RC_CLASS);
    footer.classList[hideNative ? 'add' : 'remove'](HIDE_NATIVE_CLASS);

    if (focusWasHidden) moveFocusToWidget(widget);
  }

  // Keyboard and screen-reader users focused on a control we just hid would otherwise be reset
  // to <body> with nothing announced; move them onto Loop's widget instead. A polite live-region
  // announcement to go with the move is deliberately NOT added here (reviewer finding, ECSR-1059
  // review round 2, CONSIDER): the string would have to come from a locale key, and a new key
  // raises a MatchingTranslations offence in each of the 35 locales this theme ships without a
  // translation, which fails the no-new-offences gate. It needs the translations supplied through
  // the Shopify language editor first.
  function moveFocusToWidget(widget) {
    var target = widget.querySelector
      ? widget.querySelector('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])')
      : null;
    if (!target) {
      target = widget;
      if (!target.hasAttribute('tabindex')) target.setAttribute('tabindex', '-1');
    }
    if (target.focus) target.focus();
  }

  function sameWidgets(widgets) {
    if (widgets.length !== observedWidgets.length) return false;
    for (var i = 0; i < widgets.length; i++) {
      if (widgets[i] !== observedWidgets[i]) return false;
    }
    return true;
  }

  function updateObserved(widgets) {
    if (!resizeObserver || sameWidgets(widgets)) return;
    // Amplify replaces the footer wholesale, so observing without disconnecting would retain
    // every discarded widget and its detached subtree.
    resizeObserver.disconnect();
    for (var i = 0; i < widgets.length; i++) {
      resizeObserver.observe(widgets[i]);
    }
    observedWidgets = widgets;
  }

  function sync() {
    scheduled = false;

    // Nothing to measure or unflag when the drawer is not in the DOM: skip the layout read.
    var footers = document.querySelectorAll(FOOTER_SELECTOR);
    if (!footers.length) {
      updateObserved([]);
      return;
    }

    var widgets = [];
    for (var i = 0; i < footers.length; i++) {
      var footer = footers[i];
      var widget = footer.querySelector(WIDGET_SELECTOR);
      if (widget) widgets.push(widget);

      if (widget && hasRendered(widget)) {
        applyFlags(footer, widget);
      } else {
        clearFlags(footer);
      }
    }

    updateObserved(widgets);
  }

  function schedule() {
    if (scheduled) return;
    scheduled = true;
    if (typeof requestAnimationFrame === 'function') {
      requestAnimationFrame(sync);
    } else {
      setTimeout(sync, 0);
    }
  }

  function nodeTouchesMiniCart(node) {
    if (!node || node.nodeType !== 1) return false;
    if (node.matches && node.matches(RELEVANT_SELECTOR)) return true;
    return !!(node.querySelector && node.querySelector(RELEVANT_SELECTOR));
  }

  function listTouchesMiniCart(nodes) {
    for (var i = 0; i < nodes.length; i++) {
      if (nodeTouchesMiniCart(nodes[i])) return true;
    }
    return false;
  }

  // This observer is document-wide and the script is site-wide, so every DOM change on every
  // page reaches here. Cheap relevance test first, so pages with heavy third-party churn
  // (Nosto, Okendo, lazy media) do not pay for a document query on each batch.
  function isRelevant(records) {
    for (var i = 0; i < records.length; i++) {
      var record = records[i];
      var target = record.target;
      if (target && target.nodeType === 1 && target.closest && target.closest(FOOTER_SELECTOR)) {
        return true;
      }
      if (listTouchesMiniCart(record.addedNodes)) return true;
      if (listTouchesMiniCart(record.removedNodes)) return true;
    }
    return false;
  }

  // Fail-safe, synchronous: any footer that currently holds no `checkout-plus` loses its hide
  // classes immediately, before the next paint. Amplify's measured behaviour today is a wholesale
  // re-render that produces a fresh, unflagged footer node, so this is not the current path — but
  // if Amplify ever swaps the footer's children while keeping the footer element, the new markup
  // would inherit `loop-checkout-plus-hide-rc` / `-hide-native` for ~1 frame with Loop's widget
  // not yet painted: the forbidden state. Clearing can only ever make Amplify's controls visible,
  // never hide them, so it must not wait for a measurement. Re-application still happens only in
  // the measuring `sync()` pass — nothing outside `sync()` may ever SET a hide class.
  function clearUnbackedFooters() {
    var footers = document.querySelectorAll(FOOTER_SELECTOR);
    for (var i = 0; i < footers.length; i++) {
      if (!footers[i].querySelector(WIDGET_SELECTOR)) clearFlags(footers[i]);
    }
  }

  function start() {
    schedule();
    if (typeof MutationObserver === 'function') {
      new MutationObserver(function (records) {
        if (!isRelevant(records)) return;
        clearUnbackedFooters();
        schedule();
      }).observe(document.documentElement, {
        childList: true,
        subtree: true,
      });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
